import {
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { saveAs } from 'file-saver';
import { AuthService } from '../../core/auth/auth.service';
import { ErrorService } from '../../core/services/error.service';
import { ApiService } from '../../core/transport/api.service';
import { Endpoints } from '../../core/transport/endpoints';
import { buildExportFilename } from '../../core/transport/export-filename';
import { IPaginated } from '../../core/types/http.interface';
import { IChatMessage } from '../../core/types/message.interface';
import {
  IAddress,
  ICabinetCableRequirements,
  IContactPerson,
  IProjectBOM,
  IProjectCabinet,
  IProjectCreatePayload,
  IProjectInProductionPayload,
  IProjectItem,
  IProjectInstallationConfig,
  IProjectList,
  IProjectMarkDeliveredPayload,
  IProjectPartLineItem,
  IProjectPartOption,
  IProjectProfileUpdatePayload,
  ProjectQuestionnairePayload,
  IProductionProjectListItem,
  IResellerQuotePdfRequest,
  IResellerQuotePreviewRequest,
  IResellerQuoteRequest,
  ISubmitPoPayload,
  IQuoteActivityItem,
  IQuoteGenerateOverrides,
  IQuotePreview,
  ProjectQuoteType,
  QuoteOverridePricingMode,
  WirelessServerModel,
} from '../../core/types/project.interface';

const PREVIEW_ONLY_RESELLER_LINE_ITEM_PART_NUMBERS = new Set(['INSTALLATION']);

type ResellerQuotePdfPayload = IResellerQuotePdfRequest & {
  pricing_mode?: QuoteOverridePricingMode;
};

type RoomsAndTabletsPayload = {
  rooms_count?: number;
  rooms_unknown?: boolean;
  tablets: boolean;
  wireless_server?: WirelessServerModel;
};

type ApprovePoSalesPayload = Record<string, never>;

type ApprovePoSupportPayload = {
  cable_requirements?: ICabinetCableRequirements[] | null;
};

type SaveCableRequirementsPayload = {
  cable_requirements: ICabinetCableRequirements[] | null;
};

type RejectPoPayload = {
  po_rejection_reason: string;
};

type MarkInvoicedPayload = {
  invoice_reference?: string;
};

type ProjectPoFileResponse = {
  url: string;
};

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly errorService = inject(ErrorService);

  readonly projects$ = new BehaviorSubject<IPaginated<IProjectList[]> | null>(
    null,
  );

  findMany(params: Partial<any>): Observable<IPaginated<IProjectList[]>> {
    return this.api.get<IPaginated<IProjectList[]>>(
      this.projectsListEndpoint(),
      params,
    );
  }

  findAll(params: Partial<any> = {}): void {
    this.findMany(params).subscribe((response: IPaginated<IProjectList[]>) => {
      this.projects$.next(response);
    });
  }

  findOne(projectId: string): Observable<IProjectItem> {
    return this.api.get<IProjectItem>(this.projectEndpoint(projectId));
  }

  listProductionProjects(
    params: Partial<any> = {},
  ): Observable<IPaginated<IProductionProjectListItem[]>> {
    return this.api.get<IPaginated<IProductionProjectListItem[]>>(
      Endpoints.projects.adminProductionList,
      params,
    );
  }

  getBom(projectId: string): Observable<IProjectBOM> {
    return this.api.get<IProjectBOM>(Endpoints.projects.adminBom(projectId));
  }

  downloadBom(projectId: string): Observable<HttpResponse<Blob>> {
    return this.withBlobErrorHandling(
      this.api.getBlobResponse(Endpoints.projects.adminBomCsv(projectId)),
    );
  }

  exportBulkBom(projectIds: string[]): Observable<HttpResponse<Blob>> {
    return this.withBlobErrorHandling(
      this.api.postBlobResponse(
        Endpoints.projects.adminBomBulk,
        { _id: projectIds },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      ),
    );
  }

  storeOne(payload: IProjectCreatePayload): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(this.projectsListEndpoint(), payload);
  }

  submitPo(
    projectId: string,
    payload: ISubmitPoPayload,
  ): Observable<IProjectItem> {
    const formData = new FormData();
    formData.append('po_reference', payload.po_reference.trim());
    formData.append('ordered_quote_number', payload.ordered_quote_number.trim());
    formData.append('file', payload.file);
    this.appendJsonField(
      formData,
      'shipping_address',
      payload.shipping_address,
    );
    this.appendJsonField(formData, 'shipping_attn', payload.shipping_attn);

    if (payload.site_address != null) {
      this.appendJsonField(formData, 'site_address', payload.site_address);
    }

    if (payload.site_poc != null) {
      this.appendJsonField(formData, 'site_poc', payload.site_poc);
    }

    if ((payload.site_ppe_requirements ?? '').trim().length > 0) {
      formData.append(
        'site_ppe_requirements',
        (payload.site_ppe_requirements ?? '').trim(),
      );
    }

    const cableRequirements = payload.cable_requirements ?? null;
    if ((cableRequirements ?? []).length > 0) {
      this.appendJsonField(formData, 'cable_requirements', cableRequirements!);
    }

    return this.api.upload<IProjectItem>(
      this.submitPoEndpoint(projectId),
      formData,
    );
  }

  storeQuestionnaire(
    projectId: string,
    cabinetId: string,
    payload: ProjectQuestionnairePayload,
  ): Observable<IProjectCabinet> {
    return this.api.put<IProjectCabinet>(
      this.cabinetEndpoint(projectId, cabinetId),
      payload,
    );
  }

  updateOne(projectId: string, payload: IProjectProfileUpdatePayload): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(this.projectEndpoint(projectId), payload);
  }

  updateInstallationConfig(
    projectId: string,
    config: IProjectInstallationConfig,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      this.installationConfigEndpoint(projectId),
      config,
    );
  }

  saveCableRequirements(
    projectId: string,
    payload: SaveCableRequirementsPayload,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      this.cableRequirementsEndpoint(projectId),
      payload,
    );
  }

  deleteOne(projectId: string): Observable<void> {
    return this.api.delete<void>(this.projectEndpoint(projectId));
  }

  deleteMany(ids: string[]): Observable<void> {
    return this.api.post<void>(this.bulkDeleteEndpoint(), {
      _id: ids,
    });
  }

  exportAll(ids: string[] = []): void {
    void this.api
      .downloadAndSave(
        Endpoints.projects.adminExport,
        buildExportFilename('projects'),
        { 'filter[_id]': ids.join(',') },
      )
      .subscribe();
  }

  archiveMany(ids: string[], archived: boolean): Observable<unknown> {
    return this.api.post(this.bulkArchiveEndpoint(), {
      _id: ids,
      archived,
    });
  }

  addTag(projectId: string, tag: string): Observable<string[]> {
    return this.api.post<string[]>(this.tagsEndpoint(projectId), { tag });
  }

  removeTag(projectId: string, tag: string): Observable<string[]> {
    return this.api.patch<string[]>(this.tagsEndpoint(projectId), { tag });
  }

  addCabinet(projectId: string): Observable<IProjectCabinet> {
    return this.api.post<IProjectCabinet>(this.cabinetsEndpoint(projectId), {});
  }

  archive(
    projectId: string,
    archived: boolean,
  ): Observable<{ archived: boolean }> {
    return this.api.patch<{ archived: boolean }>(
      this.archiveEndpoint(projectId),
      {
        archived,
      },
    );
  }

  removeCabinet(projectId: string, cabinetId: string): Observable<void> {
    return this.api.delete<void>(this.cabinetEndpoint(projectId, cabinetId));
  }

  updateRoomsCountAndTablets(
    projectId: string,
    payload: RoomsAndTabletsPayload,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      this.roomsAndTabletsEndpoint(projectId),
      payload,
    );
  }

  getQuotePreview(
    projectId: string,
    pricingMode?: QuoteOverridePricingMode,
  ): Observable<IQuotePreview> {
    const params =
      this.hasAdminProjectsAccess() && pricingMode != null
        ? { pricing_mode: pricingMode }
        : undefined;

    return this.api.get<IQuotePreview>(
      this.quotePreviewEndpoint(projectId),
      params,
    );
  }

  recalculateQuotePreview(
    projectId: string,
    payload: IQuoteGenerateOverrides,
  ): Observable<IQuotePreview> {
    const requestBody = this.hasAdminProjectsAccess()
      ? payload
      : this.buildResellerQuotePreviewRequest(payload);

    return this.api.post<IQuotePreview>(
      this.quotePreviewEndpoint(projectId),
      requestBody,
    );
  }

  requestSpecialPricing(projectId: string): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      this.specialPricingEndpoint(projectId),
      {},
    );
  }

  downloadPdf(
    project: IProjectItem,
    includeInstallation?: boolean,
  ): Observable<HttpResponse<Blob>> {
    const params =
      includeInstallation === true
        ? { include_installation: 'true' }
        : undefined;

    return this.withBlobErrorHandling(
      this.api.getBlobResponse(this.exportPdfEndpoint(project._id), params),
    );
  }

  downloadDatasheets(project: IProjectItem): Observable<HttpResponse<Blob>> {
    return this.withBlobErrorHandling(
      this.api.getBlobResponse(this.exportDatasheetsEndpoint(project._id)),
    );
  }

  downloadPdfWithOverrides(
    project: IProjectItem,
    payload: IQuoteGenerateOverrides,
  ): Observable<HttpResponse<Blob>> {
    const requestBody = this.hasAdminProjectsAccess()
      ? payload
      : this.buildResellerQuotePreviewRequest(payload);

    return this.withBlobErrorHandling(
      this.api.postBlobResponse(
        this.exportPdfEndpoint(project._id),
        requestBody,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      ),
    );
  }

  downloadResellerPdf(
    project: IProjectItem,
    markupPercentage: number,
    overrides?: IQuoteGenerateOverrides,
  ): Observable<HttpResponse<Blob>> {
    const hasAdminAccess = this.hasAdminProjectsAccess();
    const payload: ResellerQuotePdfPayload | IResellerQuoteRequest =
      overrides != null
        ? this.buildResellerQuotePdfRequest(
            overrides,
            markupPercentage,
            hasAdminAccess,
          )
        : {
            markup_percentage: markupPercentage,
          };

    return this.withBlobErrorHandling(
      this.api.postBlobResponse(
        this.exportResellerPdfEndpoint(project._id, hasAdminAccess),
        payload,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      ),
    );
  }

  approveSpecialPricing(projectId: string): Observable<IProjectItem> {
    return this.api.patch<IProjectItem>(
      Endpoints.projects.adminSpecialPricing(projectId),
      {
        action: 'approve',
      },
    );
  }

  denySpecialPricing(
    projectId: string,
    reason: string,
  ): Observable<IProjectItem> {
    return this.api.patch<IProjectItem>(
      Endpoints.projects.adminSpecialPricing(projectId),
      {
        action: 'deny',
        reason,
      },
    );
  }

  approvePoSales(
    projectId: string,
    payload: ApprovePoSalesPayload = {},
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminApproveSales(projectId),
      payload,
    );
  }

  approvePoSupport(
    projectId: string,
    payload: ApprovePoSupportPayload = {},
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminApproveSupport(projectId),
      payload,
    );
  }

  rejectPo(
    projectId: string,
    payload: RejectPoPayload,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminRejectPo(projectId),
      payload,
    );
  }

  markInProduction(
    projectId: string,
    payload: IProjectInProductionPayload,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkInProduction(projectId),
      payload,
    );
  }

  markDelivered(
    projectId: string,
    payload: IProjectMarkDeliveredPayload = {},
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkDelivered(projectId),
      payload,
    );
  }

  markInvoiced(
    projectId: string,
    invoiceReference?: string | null,
  ): Observable<IProjectItem> {
    const normalizedReference = invoiceReference?.trim() ?? '';
    const payload: MarkInvoicedPayload = normalizedReference.length > 0
      ? { invoice_reference: normalizedReference }
      : {};

    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkInvoiced(projectId),
      payload,
    );
  }

  markPaid(projectId: string): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkPaid(projectId),
      {},
    );
  }

  getPoFile(projectId: string): Observable<string> {
    return this.api
      .get<ProjectPoFileResponse>(Endpoints.projects.adminPoFile(projectId))
      .pipe(map((response) => response.url));
  }

  searchProjectPartOptions(
    projectId: string,
    query: string,
    limit = 20,
    division?: ProjectQuoteType | null,
  ): Observable<{ items: IProjectPartOption[] }> {
    return this.api.get<{ items: IProjectPartOption[] }>(
      this.partsOptionsEndpoint(projectId),
      {
        query,
        limit,
        ...(division && { division }),
      },
    );
  }

  updateProjectParts(
    projectId: string,
    payload: { line_items: IProjectPartLineItem[] },
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(this.partsEndpoint(projectId), payload);
  }

  updateProjectCustomPrice(
    projectId: string,
    payload: { part_number: string; unit_price_cents: number | null },
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      Endpoints.projects.adminCustomPricing(projectId),
      payload,
    );
  }

  updateProjectSpares(
    projectId: string,
    payload: { line_items: IProjectPartLineItem[] },
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.spares(projectId), payload);
  }

  getQuoteActivity(
    projectId: string,
    params: { page?: number; limit?: number } = {},
  ): Observable<IPaginated<IQuoteActivityItem[]>> {
    return this.api.get<IPaginated<IQuoteActivityItem[]>>(
      this.activityQuotesEndpoint(projectId),
      params as any,
    );
  }

  getMessages(
    projectId: string,
    params: { page?: number; limit?: number } = {},
  ): Observable<IPaginated<IChatMessage[]> & { unread_count: number }> {
    return this.api.get<IPaginated<IChatMessage[]> & { unread_count: number }>(
      this.messagesEndpoint(projectId),
      params as any,
    );
  }

  sendMessage(projectId: string, content: string): Observable<IChatMessage> {
    return this.api.post<IChatMessage>(this.messagesEndpoint(projectId), {
      content,
    });
  }

  markMessagesRead(projectId: string): Observable<void> {
    return this.api.post<void>(this.messagesReadEndpoint(projectId), {});
  }

  generatePdfFromStoredQuote(
    projectId: string,
    quoteNumber: string,
  ): Observable<HttpResponse<Blob>> {
    const fallbackName = `quote_${quoteNumber}.pdf`;

    return this.withBlobErrorHandling(
      this.api.downloadResponse(
        this.activityQuotePdfEndpoint(projectId, quoteNumber),
      ),
    ).pipe(
      tap((response: HttpResponse<Blob>) => {
        this.saveResponseBlob(response, fallbackName, 'application/pdf');
      }),
    );
  }

  private withBlobErrorHandling(
    source$: Observable<HttpResponse<Blob>>,
  ): Observable<HttpResponse<Blob>> {
    return source$.pipe(
      catchError((error) => this.normalizeBlobHttpError(error)),
      catchError((error) => this.errorService.handle(error)),
    );
  }

  private getFilenameFromContentDisposition(
    contentDisposition: string | null,
    fallback: string,
  ): string {
    if (!contentDisposition) {
      return fallback;
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
    }

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (basicMatch?.[1]) {
      return basicMatch[1];
    }

    return fallback;
  }

  private saveResponseBlob(
    response: HttpResponse<Blob>,
    fallbackName: string,
    fallbackMimeType: string,
  ): void {
    const blob = response.body ?? new Blob([], { type: fallbackMimeType });
    const fileName = this.getFilenameFromContentDisposition(
      response.headers.get('content-disposition'),
      fallbackName,
    );

    saveAs(blob, fileName);
  }

  private normalizeBlobHttpError(error: unknown): Observable<never> {
    if (
      !(error instanceof HttpErrorResponse) ||
      !(error.error instanceof Blob)
    ) {
      return throwError(() => error);
    }

    return from(
      error.error
        .text()
        .then((blobText) => {
          const parsedPayload = this.tryParseJson(blobText);
          if (parsedPayload === null) {
            return error;
          }

          return new HttpErrorResponse({
            error: parsedPayload,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          });
        })
        .catch(() => error),
    ).pipe(switchMap((normalizedError) => throwError(() => normalizedError)));
  }

  private tryParseJson(payload: string): unknown | null {
    if (payload.trim().length === 0) {
      return null;
    }

    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }

  private appendJsonField(
    formData: FormData,
    fieldName: string,
    value:
      | IAddress
      | IContactPerson
      | ICabinetCableRequirements[]
      | Record<string, unknown>,
  ): void {
    formData.append(fieldName, JSON.stringify(value));
  }

  private buildResellerQuotePreviewRequest(
    overrides: IQuoteGenerateOverrides,
  ): IResellerQuotePreviewRequest {
    const requestBody: IResellerQuotePreviewRequest = {
      line_items: overrides.line_items
        .filter(
          ({ part_number }) =>
            !PREVIEW_ONLY_RESELLER_LINE_ITEM_PART_NUMBERS.has(part_number),
        )
        .map(({ part_number, quantity }) => ({
          part_number,
          quantity,
        })),
      include_installation: overrides.include_installation,
    };

    if (Object.prototype.hasOwnProperty.call(overrides, 'spares_line_items')) {
      requestBody.spares_line_items = overrides.spares_line_items ?? [];
    }

    return requestBody;
  }

  private buildResellerQuotePdfRequest(
    overrides: IQuoteGenerateOverrides,
    markupPercentage: number,
    includePricingMode: boolean,
  ): ResellerQuotePdfPayload {
    const requestBody: ResellerQuotePdfPayload = {
      ...this.buildResellerQuotePreviewRequest(overrides),
      markup_percentage: markupPercentage,
    };

    if (includePricingMode && overrides.pricing_mode != null) {
      requestBody.pricing_mode = overrides.pricing_mode;
    }

    return requestBody;
  }

  private projectsListEndpoint(): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminList
      : Endpoints.projects.list;
  }

  private projectEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminOne(projectId)
      : Endpoints.projects.one(projectId);
  }

  private submitPoEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminSubmitPo(projectId)
      : Endpoints.projects.submitPo(projectId);
  }

  private cabinetEndpoint(projectId: string, cabinetId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminCabinet(projectId, cabinetId)
      : Endpoints.projects.cabinet(projectId, cabinetId);
  }

  private cabinetsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminCabinets(projectId)
      : Endpoints.projects.cabinets(projectId);
  }

  private installationConfigEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminInstallationConfig(projectId)
      : Endpoints.projects.installationConfig(projectId);
  }

  private bulkDeleteEndpoint(): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminBulkDelete
      : Endpoints.projects.bulkDelete;
  }

  private bulkArchiveEndpoint(): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminArchived
      : Endpoints.projects.archived;
  }

  private tagsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminTags(projectId)
      : Endpoints.projects.tags(projectId);
  }

  private archiveEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminArchiveOne(projectId)
      : Endpoints.projects.archiveOne(projectId);
  }

  private roomsAndTabletsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminRoomsAndTablets(projectId)
      : Endpoints.projects.roomsAndTablets(projectId);
  }

  private cableRequirementsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminCableRequirements(projectId)
      : Endpoints.projects.cableRequirements(projectId);
  }

  private quotePreviewEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminQuotePreview(projectId)
      : Endpoints.projects.quotePreview(projectId);
  }

  private specialPricingEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminSpecialPricing(projectId)
      : Endpoints.projects.specialPricing(projectId);
  }

  private exportPdfEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminExportPdf(projectId)
      : Endpoints.projects.exportPdf(projectId);
  }

  private exportDatasheetsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminExportDatasheets(projectId)
      : Endpoints.projects.exportDatasheets(projectId);
  }

  private exportResellerPdfEndpoint(
    projectId: string,
    hasAdminAccess = this.hasAdminProjectsAccess(),
  ): string {
    return hasAdminAccess
      ? Endpoints.projects.adminExportResellerPdf(projectId)
      : Endpoints.projects.exportResellerPdf(projectId);
  }

  private partsOptionsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminPartsOptions(projectId)
      : Endpoints.projects.partsOptions(projectId);
  }

  private partsEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminParts(projectId)
      : Endpoints.projects.parts(projectId);
  }

  private activityQuotesEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminActivityQuotes(projectId)
      : Endpoints.projects.activityQuotes(projectId);
  }

  private activityQuotePdfEndpoint(
    projectId: string,
    quoteNumber: string,
  ): string {
    const normalizedQuoteNumber = encodeURIComponent(quoteNumber);

    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminActivityQuotePdf(
          projectId,
          normalizedQuoteNumber,
        )
      : Endpoints.projects.activityQuotePdf(projectId, normalizedQuoteNumber);
  }

  private messagesEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminMessages(projectId)
      : Endpoints.projects.messages(projectId);
  }

  private messagesReadEndpoint(projectId: string): string {
    return this.hasAdminProjectsAccess()
      ? Endpoints.projects.adminMessagesRead(projectId)
      : Endpoints.projects.messagesRead(projectId);
  }

  private hasAdminProjectsAccess(): boolean {
    return this.authService.hasAdminProjectsAccessRuntimeSync;
  }
}
